import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Footer from "../components/footer/footer";
import { api, s3Url } from "../service/api.service";

const getAssetUrl = (path) => {
  if (!path) return "";
  if (path.startsWith("http://") || path.startsWith("https://")) return path;
  return `${s3Url}${path}`;
};

const AudioAlbumDetail = () => {
  const { albumId } = useParams();
  const navigate = useNavigate();
  const [album, setAlbum] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchAlbum = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get("/admin/album/allAlbumlist");
        const data = response?.data || response;
        const albums = data?.data || data || [];
        const selectedAlbum = Array.isArray(albums)
          ? albums.find((item) => item._id === albumId)
          : null;
        setAlbum(selectedAlbum || null);
      } catch (err) {
        console.error("Audio album detail API error:", err);
        setError("Unable to load the playlist. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    fetchAlbum();
  }, [albumId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF5F3] flex items-center justify-center px-4">
        <div className="text-lg text-gray-600">Loading playlist...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#FFF5F3] flex items-center justify-center px-4">
        <div className="max-w-3xl w-full text-center py-24">
          <h1 className="text-4xl font-semibold mb-4">Error</h1>
          <p className="text-gray-600 mb-8">{error}</p>
          <button
            type="button"
            onClick={() => navigate("/audio-album")}
            className="px-6 py-3 rounded-full bg-primary text-white"
          >
            Back to playlists
          </button>
        </div>
      </div>
    );
  }

  if (!album) {
    return (
      <div className="min-h-screen bg-[#FFF5F3] flex items-center justify-center px-4">
        <div className="max-w-3xl w-full text-center py-24">
          <h1 className="text-4xl font-semibold mb-4">Album not found</h1>
          <p className="text-gray-600 mb-8">
            The playlist you requested does not exist.
          </p>
          <button
            type="button"
            onClick={() => navigate("/audio-album")}
            className="px-6 py-3 rounded-full bg-primary text-white"
          >
            Back to playlists
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#FFF5F3] min-h-screen">
      <div className="max-w-7xl mx-auto px-4 py-24">
        <button
          type="button"
          onClick={() => navigate("/audio-album")}
          className="mb-8 inline-flex items-center rounded-full border border-gray-300 bg-white px-5 py-3 text-sm font-medium text-gray-700 shadow-sm"
        >
          Back to playlists
        </button>

        <div className="h-64 w-3/4 grid gap-2 rounded-[32px] overflow-hidden bg-white shadow-boxshadow2 border border-gray-200 lg:grid-cols-[1.3fr_0.9fr]">
          <div className="relative overflow-hidden bg-gray-100 h-56 w-56">
            <div className="aspect-[16/9] lg:aspect-[4/3] h-56 w-56">
              <img
                src={getAssetUrl(album.imageURL)}
                alt={album.name}
                className="w-56 h-56 object-cover"
              />
            </div>
            <div className="w-56 h-56 absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent"></div>
            {/* <div className="absolute bottom-6 left-6 text-white">
              <p className="text-xs uppercase tracking-[0.3em] text-white/80">
                Playlist
              </p>
              <h1 className="text-4xl font-semibold">{album.name}</h1>
              <p className="mt-2 text-sm text-white/80">
                {album.audiosList?.length || 0} tracks
              </p>
            </div> */}
          </div>

          <div className="p-4 flex flex-col justify-between gap-2">
            <div>
              <div className="mb-6 inline-flex rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
                Audio Playlist
              </div>
              <p className="text-gray-600 leading-relaxed">
                Enjoy this collection of bhajans with easy playback from our
                audio library. Tap any track below to play in-line.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="rounded-[24px] bg-[#FFF1F1] p-5">
                <p className="text-sm text-gray-500">Total Tracks</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  {album.audiosList?.length || 0}
                </p>
              </div>
              <div className="rounded-[24px] bg-[#F3F7FF] p-5">
                <p className="text-sm text-gray-500">Album Type</p>
                <p className="mt-2 text-3xl font-semibold text-gray-900">
                  Audio
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-10 space-y-4">
          {album.audiosList?.map((audio, index) => (
            <div
              key={audio._id}
              className="grid gap-4 rounded-[32px] border border-gray-200 bg-white p-5 shadow-sm sm:grid-cols-[auto_1fr] sm:items-center"
            >
              <div className="h-56 w-56 overflow-hidden rounded-3xl bg-gray-100">
                <img
                  src={getAssetUrl(audio.imageURL)}
                  alt={audio.title}
                  className="h-full w-full object-cover"
                />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">
                      {audio.title?.trim()}
                    </h3>
                    <p className="text-sm text-gray-600">
                      {audio.singer?.trim()}
                    </p>
                  </div>
                  <span className="rounded-full bg-primary/10 px-4 py-2 text-sm font-semibold text-primary">
                    Track {index + 1}
                  </span>
                </div>
                <audio
                  controls
                  className="w-full rounded-3xl bg-gray-100 p-3"
                  src={audio.audioURL}
                >
                  Your browser does not support the audio element.
                </audio>
              </div>
            </div>
          ))}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default AudioAlbumDetail;
